/**
 * Status Badge Component
 *
 * Color-coded badge for displaying status, severity, or priority levels.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React from 'react';
import {
  SEVERITY_COLORS,
  SeverityLevel,
  STATUS_COLORS,
  StatusLevel,
} from '../../theme/dashboardTheme';

interface StatusBadgeProps {
  /** The status/severity level */
  level: SeverityLevel | StatusLevel;
  /** Display label (if not provided, uses theme default) */
  label?: string;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md';
  /** Show pulsing dot indicator */
  showDot?: boolean;
  /** Use severity colors (default) or status colors */
  variant?: 'severity' | 'status';
  /** Custom class name */
  className?: string;
}

const SIZE_CONFIG = {
  xs: 'px-1.5 py-0.5 text-[10px]',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  level,
  label,
  size = 'sm',
  showDot = false,
  variant = 'severity',
  className = '',
}) => {
  // Get color config based on variant
  let bgClass: string;
  let textClass: string;
  let dotClass: string;
  let defaultLabel: string;

  if (variant === 'severity' && level in SEVERITY_COLORS) {
    const colors = SEVERITY_COLORS[level as SeverityLevel];
    bgClass = colors.bg;
    textClass = colors.text;
    dotClass = colors.dot;
    defaultLabel = colors.label;
  } else if (variant === 'status' && level in STATUS_COLORS) {
    const colors = STATUS_COLORS[level as StatusLevel];
    bgClass = colors.bgLight;
    textClass = colors.text;
    dotClass = colors.bg;
    defaultLabel = level.charAt(0).toUpperCase() + level.slice(1);
  } else {
    // Fallback
    bgClass = 'bg-gray-500/20';
    textClass = 'text-gray-400';
    dotClass = 'bg-gray-400';
    defaultLabel = String(level);
  }

  const displayLabel = label ?? defaultLabel;

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full font-medium
        ${bgClass} ${textClass} ${SIZE_CONFIG[size]}
        ${className}
      `}
    >
      {showDot && (
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} />
      )}
      {displayLabel}
    </span>
  );
};

export default StatusBadge;
